import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import { useCore } from 'contexts/core';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { addInvalidFrames, getInvalidFrames } from 'ducks/core';
import { isUnavailableFramesMenuVisible, showUnavailableFramesMenu } from 'ducks/sheets';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Title from 'ui/Typography/Title';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import { AppRoutes } from 'routes';
import UnavailableFramesOptions from './UnavailableFramesOptions';
import { OptionType } from './types';
import {
  reportReplaceUnavailableFrames,
  reportCreateNewUnavailableFrames
} from 'analytics/reporters';
import media from 'ui/media';
import Sheet from 'ui/Sheet/Sheet';
import { useAppDispatch } from 'ducks/hooks';

const MenuContent = styled(Box)`
  height: 100%;
  padding-bottom: 13rem;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ButtonWrapper = styled.div`
  position: sticky;
  width: 100%;
  bottom: 0;
  padding: ${({ theme }) => theme.space.xl};
  background-color: ${({ theme }) => theme.colors.white};
`;

const MenuTitle = styled(Title)`
  margin-bottom: ${({ theme }) => theme.space.l};
  ${media.mobile`
    margin-bottom: 1rem;
  `}
`;

const UnavailableFramesMenu = () => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const open = useSelector(isUnavailableFramesMenuVisible);
  const invalidFrames = useSelector(getInvalidFrames);
  const [closeAfterCreateNew, setCloseAfterCreateNew] = useState<boolean>(true);

  let optionType: OptionType | null = null;

  useEffect(() => {
    // Create new if load vpc fails with no options
    const createNew = () => {
      if (invalidFrames.length > 0) {
        history.push(AppRoutes.PLANNER_NEW);
        dispatch(showUnavailableFramesMenu(false));
      }
    };
    return coreApi.subscribe('loadVpcFailure', createNew);
  }, [coreApi, history, invalidFrames, dispatch]);

  useEffect(() => {
    //This useEffect is here because it it removes the invalid frames too slow in core.ts. Else it will open this sheet twice after clicking "Create new".
    if (invalidFrames.length === 0 && !closeAfterCreateNew) {
      dispatch(showUnavailableFramesMenu(false));
      setCloseAfterCreateNew(true);
    }
  }, [invalidFrames, closeAfterCreateNew, dispatch]);

  const reportContinueAction = () => {
    if (optionType !== null) {
      switch (optionType) {
        case OptionType.changeColour:
          reportReplaceUnavailableFrames({ change_frame: 'color' });
          break;
        case OptionType.changeDepth:
          reportReplaceUnavailableFrames({ change_frame: 'depth' });
          break;
        case OptionType.changeDepthAndColour:
          reportReplaceUnavailableFrames({ change_frame: 'depth_and_color' });
          break;
      }
    }
  };

  return (
    <Sheet visible={open}>
      <MenuContent m="xl">
        <MenuTitle type={ThemeFontStyleTypeEnum.headingMedium}>
          {formatMessage({ id: 'storageone.unavailableFramesMenuTitle' })}
        </MenuTitle>
        <UnavailableFramesOptions typeOfOption={(type: OptionType) => (optionType = type)} />
      </MenuContent>
      <ButtonWrapper>
        <Button
          type={ButtonTypeEnum.primary}
          disabled={invalidFrames.length > 0}
          text={formatMessage({ id: 'storageone.unavailableFramesMenuContinueButton' })}
          dataTestId={open ? 'continue-unavailable-frames-btn' : ''}
          onClick={() => {
            dispatch(showUnavailableFramesMenu(false));
            setCloseAfterCreateNew(true);
            reportContinueAction();
          }}
          width={'100%'}
        />
        <Button
          pt="xs"
          type={ButtonTypeEnum.secondary}
          text={formatMessage({ id: 'storageone.unavailableFramesMenuCreateNewButton' })}
          dataTestId={'create-new-unavailable-frames-btn'}
          onClick={() => {
            history.push(AppRoutes.PLANNER_NEW);
            reportCreateNewUnavailableFrames();
            dispatch(addInvalidFrames([]));
            setCloseAfterCreateNew(false);
            dispatch(showUnavailableFramesMenu(false));
          }}
          width={'100%'}
        />
      </ButtonWrapper>
    </Sheet>
  );
};

export default UnavailableFramesMenu;
